'use client';

import { useEffect, useRef } from 'react';

import { useUserInfo } from '@/context/UserInfoContext';

import { useSetScene } from './SceneContext';
import { sceneKey } from './ontology/scene';
import type { Scene } from './ontology/types';

/**
 * Tracks the feedback scene a route page represents.
 *
 * Tracked route pages call this with the scene they represent (or null while
 * route params aren't ready). SceneProvider already resets to baseline on
 * navigation; updates are keyed by scene value so an equal scene on re-render
 * does nothing and doesn't re-render scene consumers.
 */
export function useTrackedScene(scene: Scene | null): void {
  const setScene = useSetScene();
  const { userInfo } = useUserInfo();
  const isLoggedIn = !!userInfo;
  const key = scene ? sceneKey(scene) : null;

  const sceneRef = useRef(scene);
  useEffect(() => {
    sceneRef.current = scene;
  });

  // `isLoggedIn` is a dependency because SceneProvider baseline-resets the scene
  // on auth changes (to swap the home-feed endpoint). On a login/logout that does
  // not navigate, a tracked route must re-assert its scene; otherwise it would be
  // stranded at the `unknown` baseline after that reset.
  useEffect(() => {
    if (sceneRef.current) {
      setScene(sceneRef.current);
    }
  }, [key, isLoggedIn, setScene]);
}
